import { useLingui } from '@lingui/react/macro'
import { ChartDataPoint } from 'features/systemLens'
import { hoverUnit } from 'features/timeSeries/components/timeSeriesScatterplot/chartLegendLabel'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import {
  getHeldValue,
  getInterpolatedValue,
} from 'features/timeSeries/utils/chartHelpers'
import { isSteppedSensor } from 'features/timeSeries/utils/steppedPens'
import React, { MutableRefObject, useCallback, useRef } from 'react'
import { EnhancedSensorEntry } from 'types'
import uPlot from 'uplot'

interface UseInterpolatedHoverOptions {
  xAxisSensorId: string | null
  connectPoints: boolean
  chartData: ChartDataPoint[]
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  unitMapping: Record<string, string>
  updateCursorData: (
    sensorId: string,
    yValue: number | null,
    xValue: number | null,
    unit?: string
  ) => void
  setCursorPosition: (
    position: {
      x: number
      y: number
      clientX: number
      clientY: number
      xValue?: number
    } | null
  ) => void
  uPlotRef: MutableRefObject<uPlot | null>
}

export const useInterpolatedHover = ({
  xAxisSensorId,
  connectPoints,
  chartData,
  allSensorsToRender,
  enhancedSensorEntries,
  unitMapping,
  updateCursorData,
  setCursorPosition,
  uPlotRef,
}: UseInterpolatedHoverOptions) => {
  const { t } = useLingui()

  const internalRef = useRef<HTMLDivElement | null>(null)
  const { getXValueAtCursor } = useCursorData()
  const { derivedValues } = useScatterplotContext()

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (xAxisSensorId || !connectPoints || !uPlotRef.current) return

      const xValue = getXValueAtCursor(e, uPlotRef.current)
      if (xValue === null) return

      const overRect = uPlotRef.current.over.getBoundingClientRect()
      const relativeX = e.clientX - overRect.left
      const relativeY = e.clientY - overRect.top

      setCursorPosition({
        x: relativeX,
        y: relativeY,
        clientX: e.clientX,
        clientY: e.clientY,
        xValue: xValue,
      })

      allSensorsToRender.forEach((sensor) => {
        const sensorEntry = enhancedSensorEntries.find(
          (e) => e.id === sensor.id
        )
        const displayName = sensorEntry?.data?.name || sensor.id || sensor.name

        const value = isSteppedSensor(sensorEntry?.data)
          ? getHeldValue(chartData, sensor.id, xValue)
          : getInterpolatedValue(chartData, sensor.id, xValue)

        if (value !== null) {
          const unit =
            hoverUnit(
              { id: sensor.id, name: displayName },
              { derivedValues, unitMapping }
            ) ?? t`No Unit`
          updateCursorData(sensor.id, value, xValue, unit)
        }
      })
    },
    [
      xAxisSensorId,
      connectPoints,
      chartData,
      allSensorsToRender,
      enhancedSensorEntries,
      derivedValues,
      unitMapping,
      updateCursorData,
      setCursorPosition,
      getXValueAtCursor,
      uPlotRef,
      t,
    ]
  )

  return {
    internalRef,
    handleMouseMove,
  }
}
